import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { filter, firstValueFrom, Observable, share, tap } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import {
  ChatCancelRunResponse,
  ChatCreateSessionRequest,
  ChatExpert,
  ChatHealth,
  ChatSessionFile,
  ChatSessionPage,
  ChatSessionResponse,
  ChatWSTicket,
  ChatClientMessage,
  ConversationEvent,
} from './chat.model';
import { EnvironmentService } from '../services/environment.service';

/** Connects chat sessions to their HTTP and WebSocket APIs. */
@Injectable()
export class ChatService {
  /** Services. */
  private readonly envService = inject(EnvironmentService);
  private readonly http = inject(HttpClient);

  /** State. */
  private socket: WebSocketSubject<ChatClientMessage | ConversationEvent> | undefined;
  private streamSocket: WebSocketSubject<ConversationEvent> | undefined;
  private pendingMessage: ChatClientMessage | undefined;
  private connectionVersion = 0;
  readonly isConnecting = signal(false);
  readonly isConnected = signal(false);
  readonly connectionError = signal(false);

  /** The HTTP base URL of the chat API. */
  private get httpBaseUrl(): string {
    return this.webSocketBaseUrl.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:');
  }

  /** The WebSocket base URL of the chat API. */
  private get webSocketBaseUrl(): string {
    const baseUrl = this.envService.chatServiceUrl.replace(/\/$/, '');
    const socketUrl = new URL(baseUrl, window.location.origin);
    const secureConnection =
      window.location.protocol === 'https:' ||
      socketUrl.protocol === 'https:' ||
      socketUrl.protocol === 'wss:';
    socketUrl.protocol = secureConnection ? 'wss:' : 'ws:';
    return socketUrl.toString().replace(/\/$/, '');
  }

  /** The HTTP resource path for one conversation. */
  private sessionUrl(sessionId: string): string {
    return `${this.httpBaseUrl}/sessions/${sessionId}`;
  }

  /** Lists experts accessible to the current user. */
  listExperts(): Observable<ChatExpert[]> {
    return this.http.get<ChatExpert[]>(`${this.httpBaseUrl}/experts`);
  }

  /** Checks chat service availability after a load failure. */
  getHealth(): Observable<ChatHealth> {
    return this.http.get<ChatHealth>(`${this.httpBaseUrl}/health`);
  }

  /** Lists one page of the current user's conversations. */
  listSessions(page: number, perPage: number): Observable<ChatSessionPage> {
    return this.http.get<ChatSessionPage>(`${this.httpBaseUrl}/sessions`, {
      params: { page, per_page: perPage },
    });
  }

  /** Creates a conversation for the selected expert. */
  createSession(expertId: string): Observable<ChatSessionResponse> {
    const request: ChatCreateSessionRequest = { expertId };
    return this.http.post<ChatSessionResponse>(`${this.httpBaseUrl}/sessions`, request);
  }

  /** Fetches a conversation and one page of its messages. */
  getSession(sessionId: string, page = 1): Observable<ChatSessionResponse> {
    return this.http.get<ChatSessionResponse>(this.sessionUrl(sessionId), {
      params: { page, per_page: 100 },
    });
  }

  /** Deletes a conversation. */
  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(this.sessionUrl(sessionId));
  }

  /** Requests cancellation of the active run. */
  cancelRun(sessionId: string): Observable<ChatCancelRunResponse> {
    return this.http.post<ChatCancelRunResponse>(`${this.sessionUrl(sessionId)}/runs/cancel`, null);
  }

  /** Lists files attached to a conversation. */
  listSessionFiles(sessionId: string): Observable<ChatSessionFile[]> {
    return this.http.get<ChatSessionFile[]>(`${this.sessionUrl(sessionId)}/files`);
  }

  /** Uploads one file to a conversation. */
  uploadSessionFile(sessionId: string, file: File): Observable<ChatSessionFile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ChatSessionFile>(`${this.sessionUrl(sessionId)}/files`, formData);
  }

  /** Downloads a stored file with bearer authentication. */
  downloadSessionFile(sessionId: string, fileId: string): Observable<Blob> {
    return this.http.get(`${this.sessionUrl(sessionId)}/files/${fileId}/content`, {
      responseType: 'blob',
    });
  }

  /** Deletes a stored conversation file. */
  deleteSessionFile(sessionId: string, fileId: string): Observable<void> {
    return this.http.delete<void>(`${this.sessionUrl(sessionId)}/files/${fileId}`);
  }

  /** Obtains a short-lived WebSocket ticket. */
  private async fetchTicket(path: string): Promise<string> {
    const result = await firstValueFrom(
      this.http.post<ChatWSTicket>(`${this.httpBaseUrl}${path}`, null)
    );
    return result.ticket;
  }

  /** Connects the selected session's interactive socket. */
  async connectToSession(
    sessionId: string,
    expertId: string
  ): Promise<Observable<ConversationEvent>> {
    this.disconnect();
    const version = this.connectionVersion;
    this.isConnecting.set(true);
    this.connectionError.set(false);
    try {
      const ticket = await this.fetchTicket(`/ws-ticket/${sessionId}/${expertId}`);
      if (version !== this.connectionVersion) throw new Error('Connection superseded');
      const url = `${this.webSocketBaseUrl}/ws/conversations/${sessionId}/${expertId}?ticket=${encodeURIComponent(ticket)}`;
      const socket = webSocket<ChatClientMessage | ConversationEvent>({
        url,
        openObserver: {
          next: () => {
            if (this.socket !== socket) return;
            this.isConnected.set(true);
            this.isConnecting.set(false);
            if (this.pendingMessage) {
              this.socket?.next(this.pendingMessage);
              this.pendingMessage = undefined;
            }
          },
        },
        closeObserver: {
          next: event => {
            if (this.socket !== socket) return;
            this.isConnected.set(false);
            this.isConnecting.set(false);
            this.connectionError.set(event.code !== 1000);
          },
        },
      });
      this.socket = socket;
      return socket.pipe(
        filter((frame): frame is ConversationEvent => frame.type !== 'message'),
        tap({
          error: () => {
            this.connectionError.set(true);
            this.isConnected.set(false);
          },
        }),
        share()
      );
    } catch (error) {
      if (version === this.connectionVersion) {
        this.isConnecting.set(false);
        this.connectionError.set(true);
      }
      throw error;
    }
  }

  /** Opens a finite replay socket with its own one-use ticket. */
  async connectToSessionStream(
    sessionId: string,
    after?: string
  ): Promise<Observable<ConversationEvent>> {
    const version = this.connectionVersion;
    const ticket = await this.fetchTicket(`/ws-ticket-stream/${sessionId}`);
    if (version !== this.connectionVersion) throw new Error('Connection superseded');
    const cursor = after ? `&after=${encodeURIComponent(after)}` : '';
    const url = `${this.webSocketBaseUrl}/ws/conversations/${sessionId}/stream-chunks?ticket=${encodeURIComponent(ticket)}${cursor}`;
    this.streamSocket?.complete();
    this.streamSocket = webSocket<ConversationEvent>(url);
    return this.streamSocket.pipe(share());
  }

  /** Sends a prompt over the active session socket. */
  sendMessage(text: string, attachmentIds: string[] = []): void {
    if (!this.socket) throw new Error('Chat is not connected');
    const message: ChatClientMessage = { type: 'message', content: text || null, attachmentIds };
    if (this.isConnected()) {
      this.socket.next(message);
    } else if (this.isConnecting()) {
      this.pendingMessage = message;
    } else {
      throw new Error('Chat is not connected');
    }
  }

  /** Closes the current session connections. */
  disconnect(): void {
    this.connectionVersion++;
    const socket = this.socket;
    const streamSocket = this.streamSocket;
    this.socket = undefined;
    this.streamSocket = undefined;
    socket?.complete();
    streamSocket?.complete();
    this.pendingMessage = undefined;
    this.isConnected.set(false);
    this.isConnecting.set(false);
  }
}
